import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Copy, Instagram, Facebook, Loader2, PlayCircle, CheckCircle2, CircleDashed } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { errMsg } from "./mk";
import MetaConnect from "./MetaConnect";

const Conn = ({ icon: Icon, name, ok, testId }) => (
  <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-3" data-testid={testId}>
    <Icon className="h-5 w-5 shrink-0 text-slate-700" /><span className="font-semibold text-slate-800 text-sm">{name}</span>
    <span className={`ml-auto flex items-center gap-1 text-xs font-bold ${ok ? "text-emerald-700" : "text-slate-500"}`}>
      {ok ? <CheckCircle2 className="h-4 w-4" /> : <CircleDashed className="h-4 w-4" />}{ok ? "Connected" : "Not connected"}
    </span>
  </div>
);

export default function MarketingStatus({ summary, onRefresh }) {
  const [running, setRunning] = useState(false);
  if (!summary) return null;
  const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Link copied")).catch(() => toast.error("Copy blocked — select the link and copy it"));
  const runDue = async () => {
    setRunning(true);
    try { const { data } = await api.post("/admin/marketing/run-due"); toast.success(`${data.processed} due post(s) processed`); onRefresh(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setRunning(false); }
  };
  const src = summary.enquiry_sources || {};
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="mk-status">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-3 min-w-0">
        <h3 className="font-bold text-slate-900">Posting connection</h3>
        <Conn icon={Instagram} name="Instagram Business" ok={summary.instagram_connected} testId="mk-conn-instagram" />
        <Conn icon={Facebook} name="Facebook Page" ok={summary.facebook_connected} testId="mk-conn-facebook" />
        <MetaConnect onChanged={onRefresh} />
        <p className="text-xs text-slate-500">Scheduled posts are checked every 15 minutes and go out only if approved. The connection is re-checked daily.</p>
        <Button variant="outline" onClick={runDue} disabled={running} data-testid="mk-run-due-btn">
          {running ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <PlayCircle className="mr-1 h-4 w-4" />} Check due posts now
        </Button>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-3 min-w-0">
        <h3 className="font-bold text-slate-900">Enquiries by source</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {["Website", "Instagram", "Facebook", "WhatsApp"].map((s) => (
            <div key={s} className="rounded-xl bg-slate-50 p-3 text-center" data-testid={`mk-src-${s.toLowerCase()}`}>
              <div className="text-2xl font-extrabold text-blue-900">{src[s] || 0}</div><div className="text-xs text-slate-500">{s}</div>
            </div>
          ))}
        </div>
        <h4 className="text-sm font-bold text-slate-800 pt-2">Tracking links (use in bio / posts)</h4>
        {Object.entries(summary.links || {}).map(([k, url]) => (
          <div key={k} className="flex items-center gap-2 text-xs">
            <span className="w-20 capitalize font-semibold text-slate-700">{k}</span>
            <code className="flex-1 min-w-0 truncate rounded bg-slate-50 px-2 py-1 text-slate-600">{url}</code>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copy(url)} data-testid={`mk-copy-${k}`}><Copy className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}
